import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { Modal } from 'reactstrap';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { setCurrentModal } from '../thunks/setCurrentModal';
import ModalHeader from '../components/ModalHeader';
import StandardButton from '../components/StandardButton';
import {
  OrderingSelectors,
  OrderingOperations,
  OrderingConstants,
  OrderingHooks,
} from 'polygon-ordering';
import { adjustOrder } from '../thunks/adjustOrder';
import { updateLoadingDependencies } from '../slices/loadingDependencies';
import SectionHeading from '../components/SectionHeading';
import SectionContainer from '../components/SectionContainer';
import SubSection from '../components/SubSection';
import SectionButton from '../components/SectionButton';
import { LOCATION_MODAL_ID } from './LocationModal';
import FutureDateSelector from '../components/FutureDateSelector';
import moment from 'moment';
import { InlineAlert } from '../components/InlineAlert';
import { setInlineAlert } from '../slices/inlineAlert';

const { SALE_TYPE } = OrderingConstants;

export const CATERING_DETAILS_MODAL_ID = 'CATERING_DETAILS_MODAL_ID';
const {
  getKeyOrderPropertyUpdateInProgress,
  getPickupTime,
  getBufferLocation,
  getBufferPickupTime,
  getEarliestFutureOrderingDateTime,
  getBufferSaleType,
} = OrderingSelectors;

const { setBufferPickupTime } = OrderingOperations;

const { useFutureTimeSelectorMinMax } = OrderingHooks;

const CateringDetails = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  const keyOrderPropertyUpdateInProgress = useAppSelector(getKeyOrderPropertyUpdateInProgress);
  const selectedLocation = useAppSelector(getBufferLocation);

  const pickupTime = useAppSelector(getPickupTime);
  const bufferPickupTime = useAppSelector(getBufferPickupTime);

  const bufferSaleType = useAppSelector(getBufferSaleType);

  const earliestStartDate = useAppSelector(
    getEarliestFutureOrderingDateTime({
      saleType: bufferSaleType,
      location: selectedLocation,
    }),
  );

  const { timeNotInsideOrderingWindow } = useFutureTimeSelectorMinMax({
    selectedDateTime: bufferPickupTime ?? undefined,
  });

  const readyToApply =
    selectedLocation && !keyOrderPropertyUpdateInProgress && !timeNotInsideOrderingWindow;

  useEffect(() => {
    setBufferPickupTime(pickupTime || null);
  }, []);

  useEffect(() => {
    if (bufferPickupTime && earliestStartDate) {
      const pickupTimeMoment = moment(bufferPickupTime);
      if (pickupTimeMoment.isBefore(earliestStartDate)) {
        // Although this is being set to "null", the FutureDateSelector
        // component handles null values by setting the date and time to
        // a valid default value (the earliest available date). Rather than
        // double handling, we'll just let that component handle it.
        dispatch(setBufferPickupTime(null));
        dispatch(
          setInlineAlert({
            type: 'warning',
            body: t('cateringDateChangedMessage'),
            visible: true,
          }),
        );
      }
    }
  }, [bufferPickupTime, earliestStartDate]);

  useEffect(() => {
    if (!selectedLocation) {
      dispatch(setInlineAlert({ visible: false }));
    }
  }, [selectedLocation]);

  return (
    <Modal
      isOpen={true}
      scrollable
      fullscreen={deviceTypeMobile ? true : false}
      toggle={() => {
        dispatch(setCurrentModal(null));
        dispatch(updateLoadingDependencies({ handleURLParamsInProgress: false }));
      }}
      fade={true}
      modalTransition={{
        baseClass: 'rc-scale-in',
        timeout: 0,
      }}
      style={styles.modal}
    >
      <div style={styles.modalContainer}>
        <ModalHeader
          backClick={() => {
            dispatch(setCurrentModal(null));
          }}
          title={`${t(`saleType.${SALE_TYPE.CATERING}.name`)} - ${t(
            `saleTypeChildren.${SALE_TYPE.CATERING}.name`,
          )}`}
        />
      </div>

      <div style={styles.content}>
        <div>
          <SectionHeading title={t('title.schedule')} />
          <SectionContainer>
            <FutureDateSelector
              time={bufferPickupTime || undefined}
              setTime={newTime => {
                dispatch(setBufferPickupTime(newTime));
              }}
            />
            <InlineAlert />
          </SectionContainer>
        </div>
        <div>
          <SectionHeading title={t('title.selectLocation')} />
          <SectionContainer>
            {selectedLocation && (
              <SubSection
                title={selectedLocation.name}
                subtitle={(selectedLocation.address.combinedShort || '').trim()}
              />
            )}
            <SectionButton
              themeKey="newAddressButton"
              onPress={() => {
                dispatch(
                  setCurrentModal({
                    modalId: LOCATION_MODAL_ID,
                    previousModalId: CATERING_DETAILS_MODAL_ID,
                    params: {
                      saleType: SALE_TYPE.CATERING,
                      allowRedirect: false,
                    },
                  }),
                );
              }}
              label={selectedLocation ? 'Change location' : 'Add location'}
            />
          </SectionContainer>
        </div>
      </div>
      <div className="position-absolute" style={styles.modalFooter}>
        <StandardButton
          label="Continue"
          themeKey="continueButton"
          onClick={async () => {
            if (selectedLocation) {
              dispatch(
                adjustOrder({
                  saleType: SALE_TYPE.CATERING,
                  locationId: selectedLocation.id,
                }),
              );
            }
          }}
          disabled={!readyToApply}
          showSpinner={keyOrderPropertyUpdateInProgress}
          containerStyle={{ margin: 'auto' }}
        />
      </div>
    </Modal>
  );
};

export default CateringDetails;

const styles: Styles = {
  modal: {
    maxWidth: 660,
  },
  modalContainer: {
    width: '100%',
  },
  content: {
    overflow: 'auto',
    height: '100vh',
    paddingBottom: 100,
  },
  modalFooter: {
    bottom: 0,
    width: '100%',
    padding: 20,
  },
  fallbackToPickupContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: 25,
    margin: '30px 0',
  },
};
